import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = ["Football", "Basketball", "Soccer", "Track & Field", "Baseball / Softball", "Other"];
const LOCATIONS = ["Canton", "Akron", "Wooster", "New Philadelphia", "Alliance", "Other"];
const EVAL_TYPES = [
  "Individual Athlete",
  "Group Evaluation (2–10 athletes)",
  "Team Evaluation (11+ athletes)",
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  parent_name: "",
  athlete_age: "",
  sport: "",
  school: "",
  grad_year: "",
  location: "",
  location_detail: "",
  evaluation_type: "",
  preferred_times: "",
  goal: "",
  message: "",
};

export default function AssessmentForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, setHp] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (hp) { setDone(true); return; }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    if (form.location === "Other" && !form.location_detail.trim()) {
      toast.error("Please enter your city, state/province, and country.");
      return;
    }
    setLoading(true);
    try {
      const { location_detail, ...rest } = form;
      const payload = {
        ...rest,
        location: form.location === "Other" && location_detail.trim()
          ? location_detail.trim()
          : form.location,
      };
      await axios.post(`${API}/inquiries`, payload);
      setDone(true);
      toast.success("Thank you! Your information has been received.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("We couldn't submit that right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-white placeholder-white/30 transition-colors duration-300";

  return (
    <section
      id="assessment"
      data-testid="assessment-section"
      className="py-16 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
            Start Here
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Book Free Evaluation
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-md">
            Every athlete starts with a free performance evaluation. No
            commitment — just data, a plan, and a clear path to getting faster.
          </p>
          <p className="mt-4 text-white/45 text-sm max-w-md">
            Mobile speed & athletic performance training available throughout
            Northeast Ohio — Canton, Akron, Wooster, New Philadelphia, Alliance &
            surrounding communities.
          </p>
          <ul className="mt-8 space-y-3 text-white/70">
            {["Full movement & speed evaluation", "Personalized ORIGIN™ roadmap", "No obligation, no pressure"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <CheckCircle2 size={18} className="text-white/70" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 bg-[#0b0b0b] border border-white/10 p-8 md:p-12"
        >
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="assessment-success">
              <CheckCircle2 size={56} className="text-white mb-6" />
              <h3 className="font-display font-black uppercase tracking-tight text-3xl mb-3">
                You're In.
              </h3>
              <p className="text-white/60 max-w-md">
                Thank you! Your information has been received. Coach Johnson will
                review your submission and personally reach out to you soon.
              </p>
              <button
                data-testid="assessment-reset-btn"
                onClick={() => setDone(false)}
                className="mt-8 border border-white/20 text-white uppercase tracking-[0.2em] font-bold text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6" data-testid="assessment-form">
              <input type="text" name="company" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Name *</label>
                <input data-testid="form-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Full name" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Email *</label>
                <input data-testid="form-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Phone *</label>
                <input data-testid="form-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(555) 000-0000" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Parent/Guardian (if under 18)</label>
                <input data-testid="form-parent" value={form.parent_name} onChange={update("parent_name")} className={inputCls} placeholder="Parent/guardian name" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Athlete Age</label>
                <input data-testid="form-age" value={form.athlete_age} onChange={update("athlete_age")} className={inputCls} placeholder="e.g. 15" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">School</label>
                <input data-testid="form-school" value={form.school} onChange={update("school")} className={inputCls} placeholder="School name" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Graduation Year</label>
                <input data-testid="form-grad" value={form.grad_year} onChange={update("grad_year")} className={inputCls} placeholder="e.g. 2027" />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Sport</label>
                <select data-testid="form-sport" value={form.sport} onChange={update("sport")} className={`${inputCls} appearance-none`}>
                  <option value="" className="bg-[#0b0b0b]">Select sport</option>
                  {SPORTS.map((s) => <option key={s} value={s} className="bg-[#0b0b0b]">{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Location</label>
                <select data-testid="form-location" value={form.location} onChange={update("location")} className={`${inputCls} appearance-none`}>
                  <option value="" className="bg-[#0b0b0b]">Select location</option>
                  {LOCATIONS.map((s) => <option key={s} value={s} className="bg-[#0b0b0b]">{s}</option>)}
                </select>
              </div>
              {form.location === "Other" && (
                <div className="sm:col-span-2" data-testid="form-location-other-wrap">
                  <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">City, State/Province, Country *</label>
                  <input data-testid="form-location-detail" value={form.location_detail} onChange={update("location_detail")} className={inputCls} placeholder="Ontario, Canada" />
                  <p className="mt-2 text-white/40 text-xs">Athletes train with us from anywhere through Origin Speed Academy online coaching.</p>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Evaluation Type</label>
                <select data-testid="form-evaluation-type" value={form.evaluation_type} onChange={update("evaluation_type")} className={`${inputCls} appearance-none`}>
                  <option value="" className="bg-[#0b0b0b]">Select evaluation type</option>
                  {EVAL_TYPES.map((s) => <option key={s} value={s} className="bg-[#0b0b0b]">{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Preferred Training Days/Times</label>
                <input data-testid="form-preferred" value={form.preferred_times} onChange={update("preferred_times")} className={inputCls} placeholder="e.g. Weekday evenings, weekend mornings" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Primary Goal</label>
                <input data-testid="form-goal" value={form.goal} onChange={update("goal")} className={inputCls} placeholder="e.g. Faster 40 time, college recruiting" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Additional Notes</label>
                <textarea data-testid="form-message" value={form.message} onChange={update("message")} rows={3} className={`${inputCls} resize-none`} placeholder="Tell us about the athlete..." />
              </div>
              <div className="sm:col-span-2 border border-white/15 bg-black/40 p-4" data-testid="form-forms-reminder">
                <p className="text-white/60 text-sm">
                  Heads up: all athletes must complete the required liability waiver
                  before their first session.{" "}
                  <button type="button" onClick={() => document.querySelector('#forms')?.scrollIntoView({ behavior: 'smooth' })} className="text-white underline underline-offset-4 hover:text-white/70 transition-colors">
                    Download the forms
                  </button>.
                </p>
              </div>
              <div className="sm:col-span-2 mt-2">
                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-black uppercase tracking-[0.2em] font-bold px-10 py-4 hover:bg-white/85 transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Sending..." : "Book Free Evaluation"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
